import { ToastCustom } from "@components";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { vehiclesService } from "../services";
import type {
	CreateVehicleOdometerChecklistRequest,
	CreateVehicleOdometerManualRequest,
	PreventiveMaintenanceRequest,
	UpdateVehicleTelemetryRequest,
	Vehicle,
	VehicleForm,
} from "../types";
import { PREVENTIVE_MAINTENANCE_QUERY_KEY } from "./get-preventive-maintenance.query";
import {
	VEHICLE_ODOMETER_HISTORY_QUERY_KEY,
	VEHICLE_ODOMETER_LATEST_CHECKLIST_QUERY_KEY,
	VEHICLE_ODOMETER_SUMMARY_QUERY_KEY,
} from "./get-vehicles.query";

export const useCreateVehicle = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: VehicleForm) => vehiclesService.createVehicle(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["vehicles"] });
			queryClient.invalidateQueries({ queryKey: ["vehicles-lookup"] });
		},
	});
};

export const useUpdateVehicle = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: Partial<VehicleForm> }) =>
			vehiclesService.updateVehicle(id, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ["vehicles"] });
			queryClient.invalidateQueries({ queryKey: ["vehicle", variables.id] });
		},
	});
};

export const useCreatePreventiveMaintenance = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			vehicleId,
			data,
		}: {
			vehicleId: string;
			data: PreventiveMaintenanceRequest;
		}) => vehiclesService.createPreventiveMaintenance(vehicleId, data),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PREVENTIVE_MAINTENANCE_QUERY_KEY, variables.vehicleId],
			});
		},
	});
};

export const useUpdatePreventiveMaintenance = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (variables: {
			preventiveId: string;
			vehicleId: string;
			data: PreventiveMaintenanceRequest;
		}) =>
			vehiclesService.updatePreventiveMaintenance(
				variables.preventiveId,
				variables.data
			),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [PREVENTIVE_MAINTENANCE_QUERY_KEY, variables.vehicleId],
			});
		},
	});
};

export const useDeleteVehicle = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => vehiclesService.deleteVehicle(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["vehicles"] });
			queryClient.invalidateQueries({ queryKey: ["vehicles-lookup"] });
		},
	});
};

export const useUpdateVehicleStatus = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, status }: { id: string; status: number }) =>
			vehiclesService.updateVehicleStatus(id, status),
		onSuccess: (_, variables) => {
			toast(
				ToastCustom({
					title: "Status do veículo atualizado com sucesso",
					variant: "success",
				})
			);
			// Atualiza o cache do veículo para a tag de status refletir na hora
			queryClient.setQueryData<Vehicle | undefined>(
				["vehicle", variables.id],
				(old) => (old ? { ...old, status: variables.status } : old)
			);
			queryClient.invalidateQueries({ queryKey: ["vehicles"] });
			queryClient.invalidateQueries({ queryKey: ["vehicle", variables.id] });
			queryClient.invalidateQueries({ queryKey: ["vehicles-lookup"] });
		},
		onError: () => {
			toast(
				ToastCustom({
					title: "Erro ao atualizar status do veículo",
					variant: "error",
				})
			);
		},
	});
};

export const useCreateVehicleOdometerChecklist = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			vehicleId,
			payload,
		}: {
			vehicleId: string;
			payload: CreateVehicleOdometerChecklistRequest;
		}) => vehiclesService.createVehicleOdometerChecklist(vehicleId, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [VEHICLE_ODOMETER_SUMMARY_QUERY_KEY, variables.vehicleId],
			});
			queryClient.invalidateQueries({
				queryKey: [VEHICLE_ODOMETER_HISTORY_QUERY_KEY, variables.vehicleId],
			});
			queryClient.invalidateQueries({
				queryKey: ["vehicle", variables.vehicleId],
			});
		},
	});
};

export const useUpdateVehicleTelemetry = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			vehicleId,
			payload,
		}: {
			vehicleId: string;
			payload: UpdateVehicleTelemetryRequest;
		}) => vehiclesService.updateVehicleTelemetry(vehicleId, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: ["vehicle", variables.vehicleId],
			});
			queryClient.invalidateQueries({ queryKey: ["vehicles"] });
		},
	});
};

export const useCreateVehicleOdometerManual = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			vehicleId,
			payload,
		}: {
			vehicleId: string;
			payload: CreateVehicleOdometerManualRequest;
		}) => vehiclesService.createVehicleOdometerManual(vehicleId, payload),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({
				queryKey: [VEHICLE_ODOMETER_SUMMARY_QUERY_KEY, variables.vehicleId],
			});
			queryClient.invalidateQueries({
				queryKey: [VEHICLE_ODOMETER_HISTORY_QUERY_KEY, variables.vehicleId],
			});
			queryClient.invalidateQueries({
				queryKey: [
					VEHICLE_ODOMETER_LATEST_CHECKLIST_QUERY_KEY,
					variables.vehicleId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: ["vehicle", variables.vehicleId],
			});
		},
	});
};
